import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { MultiSelect, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { useEmployeeOptions, useEmployeesByIds } from '@/api/employees';
import type { TicketFormValues } from '../ticket-form-schema';

const twoColGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--vn-space-4)',
} as const;

/**
 * Assignees section — technical, CS, sale multi-select using @vnetwork/ui MultiSelect.
 *
 * MultiSelect handles its own internal search (client-side filter over options[]).
 * useEmployeeOptions('') fetches the default 50-employee list; useEmployeesByIds
 * resolves any preselected IDs that aren't in that list (edit prefill) so chips
 * always display names rather than raw ID strings.
 *
 * number[] ↔ string[] conversion at the boundary (MultiSelect is generic over string).
 */
export function AssigneesSection() {
  const { watch, setValue } = useFormContext<TicketFormValues>();

  const techAssignees = watch('ticket_technical_assignees') ?? [];
  const csAssignees = watch('ticket_cs_assignees') ?? [];
  const saleAssignees = watch('ticket_sale_assignees') ?? [];

  // Base option list — empty query returns up to 50 employees (sufficient for
  // the internal search filter; expand page_size if teams grow beyond 50).
  const { data: baseOptions = [] } = useEmployeeOptions('');

  // Resolve any preselected IDs that may not be in the base list.
  const allSelectedIds = useMemo(
    () => [...new Set([...techAssignees, ...csAssignees, ...saleAssignees])],
    [techAssignees, csAssignees, saleAssignees],
  );
  const { data: resolvedNames = [] } = useEmployeesByIds(allSelectedIds);

  // Merge resolved names into base options (resolved first so dedup keeps name).
  const options = useMemo(() => {
    const seen = new Set<number>();
    return [...resolvedNames, ...baseOptions]
      .filter((e) => {
        if (seen.has(e.employee_id)) return false;
        seen.add(e.employee_id);
        return true;
      })
      .map((e) => ({ value: String(e.employee_id), label: e.employee_name }));
  }, [baseOptions, resolvedNames]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assignees</CardTitle>
      </CardHeader>
      <CardBody>
      <div style={twoColGrid}>
        <MultiSelect
          placeholder="Technical Assignees"
          options={options}
          value={techAssignees.map(String)}
          onChange={(vals) => setValue('ticket_technical_assignees', vals.map(Number))}
        />
        <MultiSelect
          placeholder="CS Assignees"
          options={options}
          value={csAssignees.map(String)}
          onChange={(vals) => setValue('ticket_cs_assignees', vals.map(Number))}
        />
        <MultiSelect
          placeholder="Sale Assignees"
          options={options}
          value={saleAssignees.map(String)}
          onChange={(vals) => setValue('ticket_sale_assignees', vals.map(Number))}
        />
      </div>
      </CardBody>
    </Card>
  );
}
